/**
 * Feedback Form Component (Enhanced)
 * 
 * Matches inspiration design with:
 * - User avatar + Category dropdown in header
 * - Title input
 * - WYSIWYG description editor
 * - Submit button
 */
import { useState, useEffect } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import SimpleRichEditor from '../../components/SimpleRichEditor';

// Default categories fallback
const defaultCategories = {
    'feature_requests': __( 'Feature Request', 'changeloger' ),
    'bug_reports': __( 'Bug Report', 'changeloger' ),
    'integrations': __( 'Integration', 'changeloger' ),
    'languages': __( 'Language', 'changeloger' )
};

const categoryIcons = {
    'feature_requests': '💡',
    'bug_reports': '🐛',
    'integrations': '🔗',
    'languages': '🌐'
};

const FeedbackForm = ({ categories = {}, activeCategory, onSubmit, onClose, currentUser }) => {
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
    
    // Use provided categories or fallback to defaults
    const categoryOptions = Object.keys(categories).length > 0 ? categories : defaultCategories;
    const [category, setCategory] = useState(activeCategory || Object.keys(categoryOptions)[0]);
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!title.trim()) return;

        setSubmitting(true);
        await onSubmit({ title, content, category });
        setSubmitting(false);
    };

    const getCategoryLabel = (key) => {
        return categoryOptions[key] || key;
    };

    const getCategoryIcon = (key) => {
        return categoryIcons[key] || '📝';
    };

    return (
        <div className="cha-modal-overlay active" onClick={(e) => {
            if (e.target.classList.contains('cha-modal-overlay')) onClose();
        }}>
            <div className="cha-feedback-form-modal">
                {/* Header */}
                <div className="cha-form-header">
                    <div className="cha-form-header-left">
                        {/* User Avatar */}
                        <div className="cha-form-avatar">
                            {currentUser?.avatar ? (
                                <img src={currentUser.avatar} alt="" />
                            ) : (
                                <div className="cha-avatar-placeholder">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                    </svg>
                                </div>
                            )}
                        </div>
                        
                        <span className="cha-form-separator">›</span>
                        
                        {/* Category Dropdown */}
                        <div className="cha-category-selector">
                            <button 
                                type="button"
                                className="cha-category-btn"
                                onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                            >
                                <span className="cha-category-icon">{getCategoryIcon(category)}</span>
                                {getCategoryLabel(category)}
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="14" height="14">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>
                            
                            {showCategoryDropdown && (
                                <div className="cha-category-dropdown">
                                    {Object.entries(categoryOptions).map(([key, label]) => (
                                        <button
                                            key={key}
                                            type="button"
                                            className={`cha-category-option ${category === key ? 'selected' : ''}`}
                                            onClick={() => {
                                                setCategory(key);
                                                setShowCategoryDropdown(false);
                                            }}
                                        >
                                            <span className="cha-category-icon">{getCategoryIcon(key)}</span>
                                            {label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                    
                    {/* Close Button */}
                    <button type="button" className="cha-form-close" onClick={onClose}>
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Form Content */}
                <form onSubmit={handleSubmit} className="cha-form-content">
                    {/* Title Input */}
                    <input
                        type="text"
                        className="cha-form-title-input"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder={__( 'Title of your post', 'changeloger' )}
                        required
                    />

                    {/* Description Editor */}
                    <div className="cha-form-editor-wrapper">
                        <SimpleRichEditor
                            value={content}
                            onChange={setContent}
                            placeholder={__( 'Post description...', 'changeloger' )}
                            minHeight={150}
                        />
                    </div>

                    {/* Footer */}
                    <div className="cha-form-footer">
                        <button 
                            type="submit" 
                            className="cha-submit-btn"
                            disabled={submitting || !title.trim()}
                        >
                            {submitting ? __( 'Submitting...', 'changeloger' ) : __( 'Submit Post', 'changeloger' )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default FeedbackForm;
